import React, { useCallback, useEffect, useState } from "react";
import { db, storage } from "../../../firebase";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { v4 as uuidv4 } from "uuid";
import "./admin.css";

interface PressLink {
  id: string;
  url: string;
  label?: string;
}

interface PressRelease {
  id: string;
  title: string;
  content: string;
  date: Timestamp;
  image_url?: string;
  links: PressLink[];
}

type Form = {
  title: string;
  content: string;
  date: string; // yyyy-mm-dd
};

const emptyForm: Form = { title: "", content: "", date: "" };

export const AdminPressPage = () => {
  const [headerImage, setHeaderImage] = useState("");
  const [headerFile, setHeaderFile] = useState<File | null>(null);
  const [headerUploading, setHeaderUploading] = useState(false);

  const [items, setItems] = useState<PressRelease[]>([]);
  const [form, setForm] = useState<Form>(emptyForm);
  const [linkRows, setLinkRows] = useState<PressLink[]>([]);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    const q = query(collection(db, "press_releases"), orderBy("date", "desc"));
    const snap = await getDocs(q);
    setItems(
      snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as PressRelease[]
    );
  }, []);

  useEffect(() => {
    load();
    const fetchHeader = async () => {
      const snap = await getDoc(doc(db, "site_content", "press_releases_header"));
      if (snap.exists()) setHeaderImage(snap.data().header_image || "");
    };
    fetchHeader();
  }, [load]);

  const handleHeaderUpload = async () => {
    if (!headerFile) return;
    setHeaderUploading(true);
    const storageRef = ref(storage, `press/header_${Date.now()}`);
    const uploadTask = uploadBytesResumable(storageRef, headerFile);

    uploadTask.on("state_changed", undefined, (err) => {
      console.error(err);
      setHeaderUploading(false);
    }, async () => {
      const url = await getDownloadURL(uploadTask.snapshot.ref);
      await setDoc(doc(db, "site_content", "press_releases_header"), { header_image: url });
      setHeaderImage(url);
      setHeaderFile(null);
      setHeaderUploading(false);
    });
  };

  const onFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const addLinkRow = () => {
    setLinkRows((prev) => [...prev, { id: uuidv4(), url: "", label: "" }]);
  };

  const updateLinkRow = (id: string, field: "url" | "label", value: string) => {
    setLinkRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  };

  const removeLinkRow = (id: string) => {
    setLinkRows((prev) => prev.filter((row) => row.id !== id));
  };

  const resetForm = (clearMessage: boolean = true) => {
    setEditingId(null);
    setForm(emptyForm);
    setLinkRows([]);
    setImageFile(null);
    setProgress(0);
    if (clearMessage) setMsg("");
  };

  const startEdit = (item: PressRelease) => {
    setEditingId(item.id);
    setForm({
      title: item.title || "",
      content: item.content || "",
      date: item.date?.toDate?.().toISOString().slice(0, 10) || "",
    });
    setLinkRows(item.links || []);
    setImageFile(null);
    setProgress(0);
    setMsg("");
  };

  const handleDelete = async (item: PressRelease) => {
    if (!window.confirm(`Ștergi comunicatul „${item.title}”?`)) return;
    try {
      await deleteDoc(doc(db, "press_releases", item.id));
      setMsg("✔️ Comunicat șters.");
      if (editingId === item.id) resetForm(false);
      load();
    } catch (e) {
      console.error(e);
      setMsg("❌ Eroare la ștergere.");
    }
  };

  const uploadImage = async (): Promise<string> => {
    if (!imageFile) return "";
    const storageRef = ref(storage, `press/images/${Date.now()}_${imageFile.name}`);
    const task = uploadBytesResumable(storageRef, imageFile);
    return new Promise((resolve, reject) => {
      task.on(
        "state_changed",
        (s) => setProgress(Math.round((s.bytesTransferred / s.totalBytes) * 100)),
        reject,
        async () => resolve(await getDownloadURL(task.snapshot.ref))
      );
    });
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim() || !form.date) {
      setMsg("Titlul, conținutul și data sunt obligatorii.");
      return;
    }

    const cleanLinks = linkRows
      .map((l) => ({ ...l, url: l.url.trim(), label: (l.label || "").trim() }))
      .filter((l) => l.url);

    try {
      setSaving(true);
      setMsg("");
      setProgress(0);

      let imageUrl = editingId
        ? items.find((i) => i.id === editingId)?.image_url || ""
        : "";
      if (imageFile) {
        imageUrl = await uploadImage();
      }

      const payload = {
        title: form.title.trim(),
        content: form.content.trim(),
        date: Timestamp.fromDate(new Date(form.date)),
        image_url: imageUrl,
        links: cleanLinks,
        updatedAt: serverTimestamp(),
      };

      if (editingId) {
        await updateDoc(doc(db, "press_releases", editingId), payload);
        setMsg("✔️ Comunicat actualizat.");
      } else {
        await addDoc(collection(db, "press_releases"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
        setMsg("✔️ Comunicat adăugat.");
      }

      resetForm(false);
      load();
    } catch (err) {
      console.error(err);
      setMsg("❌ Eroare la salvare.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-container">
      <h1>Administrare Știri</h1>

      <label>Imagine Banner (afișată în capul paginii publice):</label>
      <input
        type="file"
        accept="image/*"
        onChange={(e) => setHeaderFile(e.target.files?.[0] || null)}
      />
      <button type="button" onClick={handleHeaderUpload} disabled={!headerFile || headerUploading}>
        {headerUploading ? "Se încarcă..." : "Încarcă imagine"}
      </button>
      {headerImage && <img src={headerImage} alt="Header" className="preview-img" />}

      <hr />

      <h2>{editingId ? "Editează comunicat" : "Adaugă comunicat nou"}</h2>
      <form onSubmit={onSubmit}>
        <input
          type="text"
          name="title"
          placeholder="Titlu"
          value={form.title}
          onChange={onFormChange}
        />
        <textarea
          name="content"
          placeholder="Conținut complet"
          value={form.content}
          onChange={onFormChange}
        />
        <input type="date" name="date" value={form.date} onChange={onFormChange} />

        <label>Imagine comunicat (opțional):</label>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => setImageFile(e.target.files?.[0] || null)}
        />

        <div className="links-section">
          <label>Video-uri / link-uri (opțional — YouTube, Vimeo, fișier video sau orice alt link):</label>
          {linkRows.map((row) => (
            <div key={row.id} className="link-row">
              <input
                type="text"
                placeholder="https://..."
                value={row.url}
                onChange={(e) => updateLinkRow(row.id, "url", e.target.value)}
              />
              <input
                type="text"
                placeholder="Text afișat (opțional)"
                value={row.label || ""}
                onChange={(e) => updateLinkRow(row.id, "label", e.target.value)}
              />
              <button type="button" onClick={() => removeLinkRow(row.id)}>
                Șterge
              </button>
            </div>
          ))}
          <button type="button" onClick={addLinkRow}>
            + Adaugă link / video
          </button>
        </div>

        <div className="form-actions">
          <button className="save-btn" type="submit" disabled={saving}>
            {saving
              ? `Se salvează... ${progress ? progress + "%" : ""}`
              : editingId
              ? "Actualizează comunicatul"
              : "Adaugă comunicat"}
          </button>
          {editingId && (
            <button type="button" onClick={() => resetForm()}>
              Renunță
            </button>
          )}
        </div>
        {msg && <p className="admin-message">{msg}</p>}
      </form>

      <hr />

      <h2>Comunicate existente</h2>
      {items.length === 0 && <p>— niciun comunicat —</p>}
      {items.map((item) => (
        <div key={item.id} className="press-item">
          <strong>{item.title}</strong>
          <p>{item.date?.toDate?.().toLocaleDateString("ro-RO")}</p>
          {item.image_url && (
            <img src={item.image_url} alt="Preview" className="press-preview-img" />
          )}
          {item.links && item.links.length > 0 && (
            <p className="links-count">{item.links.length} link(uri)/video(uri) atașate</p>
          )}
          <div className="item-actions">
            <button type="button" className="edit-btn" onClick={() => startEdit(item)}>
              Editează
            </button>
            <button type="button" className="delete-btn" onClick={() => handleDelete(item)}>
              Șterge
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};
